import React, { useEffect, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"

import { UsersApiProvider, useUsersApi } from "../Services/UsersApiProvider"
import { AlertProvider, useAlert } from "../Components/AlertContext"
import { ConfirmationProvider, useConfirmation } from "../Components/ConfirmationContext"
import Breadcrumbs from "../Components/Breadcrumbs"
import { CustomInput, CustomToggle } from "../Components/CustomFormElements"
import Button from "../Components/Buttons"
import PageLoader from "../Components/PageLoader"
import {useAuth} from "../Contexts/AuthContext";

function UserContent({ canEdit }) {
  const { userId } = useParams()
  const isNew = userId === "new-user"
  const navigate = useNavigate()
  const { showAlert } = useAlert()
  const { status } = useAuth();

  const { getUserById, createUser, updateUser, sendResetPasswordEmail } = useUsersApi()

  const [user, setUser] = useState(null)
  const { showConfirmation } = useConfirmation();
  const [isActive, setIsActive] = useState(true)
  const [isVerified, setIsVerified] = useState(true)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [receiveSystemNotifications, setReceiveSystemNotifications] = useState(false)

  useEffect(() => {
    if (isNew) {
      setUser({ name: "", email: "", password: "", is_active: true })
      setIsActive(true)
      setReceiveSystemNotifications(false)
      setLoading(false)
    } else {
      const fetchUser = async () => {
        try {
          const data = await getUserById(userId)
          setUser(data)
          setIsActive(data.is_active)
          setIsVerified(data.is_verified)
          setReceiveSystemNotifications(data.notify_system)
        } catch (error) {
          console.error("Failed to load user", error)
        } finally {
          setLoading(false)
        }
      }
      fetchUser()
    }
  }, [userId, isNew, getUserById])

  const handleSave = async () => {
    setSaving(true)
    try {
      const payload = {
        name: user.name,
        email: user.email,
        is_active: isActive,
        is_verified: isVerified,
        notify_system: receiveSystemNotifications
      }

      if (user.password) {
        payload.password = user.password
      }

      if (isNew) {
        await createUser(payload)
        showAlert("success", "User successfully created!")
      } else {
        await updateUser(userId, payload)
        showAlert("success", "User successfully updated!")
      }

      navigate(-1)
    } catch (error) {
      console.error("Error saving user", error)
      showAlert("fail", `Failed to save user: ${error.response?.data?.error || error.response?.statusText || "error"}`)
    } finally {
      setSaving(false)
    }
  }

  const handelResetPassword = async () => {
    showConfirmation(
            `Are you sure you want to send a password reset email to this user?`,
            async () => {
                try {
                    await sendResetPasswordEmail(user.email);
                    showAlert('success', 'Password reset email was successfully sent!');
                } catch (error) {
                    showAlert('fail', 'Failed to send password reset email!');
                }
            }
        );
  }

  if (loading) return <PageLoader />
  if (!user) return <div className="container">User not found</div>

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row">
        <div className="col-6 radius-yes p-2 bg-yes">
          <h2 className="h2-title">{isNew ? "Create User" : "Edit User"}</h2>

          <div className="input-block">
            <CustomInput
              id="name"
              label="Name"
              value={user.name}
              placeholder="Enter name"
              onChange={(id, val) => setUser(prev => ({ ...prev, name: val }))}
              disabled={!canEdit}
            />
          </div>

          <div className="input-block">
            <CustomInput
              id="email"
              label="Email"
              value={user.email}
              placeholder="Enter email"
              onChange={(id, val) => setUser(prev => ({ ...prev, email: val }))}
              disabled={!(isNew && canEdit)}
            />
          </div>

          <div className="input-block">
            <CustomInput
              id="password"
              label="Password"
              value={user.password}
              placeholder={isNew ? "Enter password" : "••••••••••"}
              onChange={(id, val) => setUser(prev => ({ ...prev, password: val }))}
              type="password"
              disabled={!(isNew && canEdit)}
            />
          </div>
          {!isNew && status?.permissions?.includes("all:organizations") && (
              <div className="input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Reset Password</div>
                  <div className="small-font">Send an email to allow the user to set a new password</div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <Button background="color2" size="medium" onClick={handelResetPassword} disabled={!canEdit}>
                    Reset
                  </Button>
                </div>
              </div>
          )}

          <div className="input-block d-flex flex-row justify-between">
            <div className="input-description">
              <div className="input-title">System Notifications</div>
              <div className="small-font">
                Enable or disable receiving system notifications
              </div>
            </div>
            <div className="align-wrapper d-flex align-center">
              <CustomToggle
                  id="system-notifications-toggle"
                  label={receiveSystemNotifications ? "Enabled" : "Disabled"}
                  checked={receiveSystemNotifications}
                  onChange={(id, value) => setReceiveSystemNotifications(value)}
                  disabled={!canEdit}
              />
            </div>
          </div>

          <div className="input-block d-flex flex-row justify-between">
            <div className="input-description">
              <div className="input-title">Active</div>
              <div className="small-font">Set the User to be active or inactive</div>
            </div>
            <div className="align-wrapper d-flex align-center">
              <CustomToggle
                  id="active-toggle"
                  label={isActive ? "Active" : "Inactive"}
                  checked={isActive}
                  onChange={(id, value) => setIsActive(value)}
                  disabled={!canEdit}
              />
            </div>
          </div>

          <div className="input-block d-flex flex-row justify-between">
            <div className="input-description">
              <div className="input-title">Verified</div>
              <div className="small-font">Set the User to be verified or unverified</div>
            </div>
            <div className="align-wrapper d-flex align-center">
              <CustomToggle
                  id="verified-toggle"
                  label={isVerified ? "Verified" : "Unverified"}
                  checked={isVerified}
                  onChange={(id, value) => setIsVerified(value)}
                  disabled={!canEdit}
              />
            </div>
          </div>

          <div className="buttons-container d-flex justify-end" style={{gap: "12px"}}>
            <Button background="color2" size="medium" onClick={handleSave} disabled={saving || !canEdit}>
              Save
            </Button>
            <Button variant="danger" size="medium" onClick={() => navigate(-1)}>
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

function User({ canEdit }) {
  return (
    <AlertProvider>
      <ConfirmationProvider>
        <UsersApiProvider>
          <UserContent canEdit={canEdit} />
        </UsersApiProvider>
      </ConfirmationProvider>
    </AlertProvider>
  )
}

export default User
